import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { peopleApi, groupsApi, eventsApi, sharingApi, pantryApi } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../components/shared/Toast";
import SavingNotice from "../components/shared/SavingNotice";
import ActionMenu from "../components/shared/ActionMenu";
import ModuleHeader from "../components/shared/ModuleHeader";
import CenteredModal from "../components/shared/CenteredModal";
import BottomSheet from "../components/shared/BottomSheet";
import DangerConfirmModal from "../components/shared/ConfirmModal";
import useMinLoad from "../hooks/useMinLoad";
import useOnEscape from "../hooks/useOnEscape";
import useFormPresentation from "../hooks/useFormPresentation";

const IMG_EVENTS = "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1400&q=75&auto=format&fit=crop";

function EventActionSheet({ title, subtitle, actions, onClose }) {
  return (
    <BottomSheet title={title} label={title} onClose={onClose} bodyClassName="event-action-sheet-body">
      {subtitle ? <p className="event-action-subtitle">{subtitle}</p> : null}
      <div className="event-action-list">
        {actions.filter(Boolean).map(action => (
          <button
            key={action.key}
            type="button"
            className={`event-action-row${action.danger ? " is-danger" : ""}`}
            onClick={() => {
              onClose();
              window.setTimeout(action.onClick, 0);
            }}
            disabled={action.disabled}
          >
            <span className="event-action-icon">{action.icon}</span>
            <span>
              <strong>{action.label}</strong>
              {action.detail ? <em>{action.detail}</em> : null}
            </span>
            <span className="event-action-chevron">›</span>
          </button>
        ))}
      </div>
    </BottomSheet>
  );
}

const EVENT_TYPES = [
  { value:"trip",              label:"Trip / Vacation",    icon:"✈", hasTrip:true },
  { value:"concert",          label:"Concert / Show",     icon:"🎵", hasTrip:false },
  { value:"wedding",          label:"Wedding",            icon:"💍", hasTrip:false },
  { value:"birthday_party",   label:"Birthday Party",     icon:"🎂", hasTrip:false },
  { value:"graduation_party", label:"Graduation Party",   icon:"🎓", hasTrip:false },
  { value:"holiday_gathering",label:"Holiday Gathering",  icon:"🎄", hasTrip:false },
  { value:"game_night",       label:"Game Night",         icon:"🎲", hasTrip:false },
  { value:"anniversary_dinner",label:"Anniversary",icon:"🕯", hasTrip:false },
  { value:"baby_shower",      label:"Baby Shower",        icon:"🍼", hasTrip:false },
  { value:"other_event",      label:"Other Event",        icon:"📅", hasTrip:false },
];

const TRIP_STYLES = ["Beach","City break","Adventure","Road trip","Cruise","Ski / Winter","Cultural","Family reunion"];

function titleCase(value = "") {
  return String(value || "")
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function eventTypeLabel(value) {
  return EVENT_TYPES.find(type => type.value === value)?.label || titleCase(value || "Event");
}

function urgencyClass(days) {
  if (days === null || days === undefined) return "is-calm";
  if (days <= 14) return "is-urgent";
  if (days <= 30) return "is-soon";
  return "is-calm";
}

function eventEdgeClass({ days, isPast = false, isReady = false }) {
  if (isPast) return "is-past";
  if (isReady) return "is-ready";
  return urgencyClass(days);
}

function getEventDisplayTitle(event) {
  if (!event) return "Event";
  if (event.title) return event.title;
  const type = event.occasion_type || event.event_type;
  const destination = event.destination || event.notes?.match(/Destination: ([^|]+)/)?.[1]?.trim();
  const style = event.trip_style || event.notes?.match(/Style: ([^|]+)/)?.[1]?.trim();
  if (type === "trip") {
    if (destination && style) return `${destination} ${String(style).toLowerCase()} trip`;
    if (destination) return `${destination} trip`;
    return "Trip";
  }
  const person = event.person_name;
  const label = eventTypeLabel(type).replace(" / Vacation", "");
  if (person && !/myself/i.test(person)) return `${person}'s ${label.toLowerCase()}`;
  return label;
}

function formatShortDate(value) {
  if (!value) return "";
  return new Date(`${value}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function parseDateOnly(value) {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function StatPill({ children, tone = "" }) {
  return <span className={`events-hero-pill${tone ? ` is-${tone}` : ""}`}>{children}</span>;
}

const EVENT_WORKFLOW_DEFAULT = {
  category: "event",
  supports_gifts: false,
  supports_registry: false,
  supports_trip_plan: false,
  supports_share: true,
  group_only: false,
  personal_primary_action: "open_event",
  group_primary_action: "share_event",
};

const EVENT_WORKFLOW_MAP = {
  trip: {
    category: "trip",
    supports_trip_plan: true,
    personal_primary_action: "trip",
    group_primary_action: "trip",
  },
  wedding: {
    category: "event",
    personal_primary_action: "open_event",
    group_primary_action: "share_event",
  },
  graduation_party: {
    category: "event",
    personal_primary_action: "open_event",
    group_primary_action: "share_event",
  },
  baby_shower: {
    category: "event",
    personal_primary_action: "open_event",
    group_primary_action: "share_event",
  },
  birthday_party: {
    category: "event",
    personal_primary_action: "open_event",
    group_primary_action: "share_event",
  },
  anniversary_dinner: {
    category: "event",
    personal_primary_action: "open_event",
    group_primary_action: "share_event",
  },
  holiday_gathering: {
    category: "shared_event",
    group_only: true,
    personal_primary_action: "add_to_group",
    group_primary_action: "share_event",
  },
  game_night: {
    category: "shared_event",
    group_only: true,
    personal_primary_action: "add_to_group",
    group_primary_action: "share_event",
  },
  concert: {
    category: "event",
    personal_primary_action: "open_event",
    group_primary_action: "share_event",
  },
  other_event: {
    category: "event",
    personal_primary_action: "open_event",
    group_primary_action: "share_event",
  },
};

function getEventWorkflow(eventType, workflow) {
  if (workflow) return { ...EVENT_WORKFLOW_DEFAULT, ...workflow };
  return { ...EVENT_WORKFLOW_DEFAULT, ...(EVENT_WORKFLOW_MAP[eventType] || {}) };
}

function buildPersonalTripUrl(event, tab = "") {
  const destination = event.destination || event.notes?.match(/Destination: ([^|]+)/)?.[1]?.trim() || "";
  const style = event.trip_style || event.notes?.match(/Style: ([^|]+)/)?.[1]?.trim() || "";
  const endDate = event.end_date || event.notes?.match(/End date: ([^|]+)/)?.[1]?.trim() || "";
  const startDate = event.event_date || event.occasion_date || event.next_occurrence || "";
  let duration = 7;
  if (endDate && startDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    duration = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
  }
  const tabParam = tab ? `&tab=${encodeURIComponent(tab)}` : "";
  return `/app/trip/event/${event.id}?destination=${encodeURIComponent(destination)}&style=${encodeURIComponent(style)}&duration=${duration}&travelers=${encodeURIComponent(event.person_name || "Me")}${tabParam}`;
}

function buildPersonalEventPlanUrl(event, tab = "") {
  return `/app/event-plan/event/${event.id}${tab ? `?tab=${encodeURIComponent(tab)}` : ""}`;
}

function buildGroupTripUrl(event) {
  const duration = Math.max(1, Math.round((new Date(event.end_date || event.event_date) - new Date(event.event_date)) / (1000 * 60 * 60 * 24))) || 7;
  return `/app/trip/group/${event.id}?destination=${encodeURIComponent(event.destination || "")}&duration=${duration}`;
}

function buildGroupEventPlanUrl(event) {
  return `/app/event-plan/group/${event.id}`;
}

function mergeEventById(events, event) {
  if (!event?.id) return events;
  const exists = events.some(item => item.id === event.id);
  if (exists) {
    return events.map(item => item.id === event.id ? { ...item, ...event } : item);
  }
  return [event, ...events];
}

function parsePersonalEventInitial(event) {
  if (!event) return null;
  const forceGroup = Boolean(event._forceGroup);
  // Support both direct fields (new PersonalEvent) and note-embedded fields (legacy Occasion)
  const destination = event.destination || event.notes?.match(/Destination: ([^|]+)/)?.[1]?.trim() || "";
  const tripStyle   = event.trip_style  || event.notes?.match(/Style: ([^|]+)/)?.[1]?.trim()        || "";
  const endDate     = event.end_date    || event.notes?.match(/End date: ([^|]+)/)?.[1]?.trim()      || "";
  const cleanNotes  = event.destination
    ? (event.notes || "")   // new: notes are already clean
    : (event.notes || "")
        .split(" | ")
        .filter(part => !part.startsWith("Destination: ") && !part.startsWith("Style: ") && !part.startsWith("End date: ") && !part.startsWith("For: "))
        .join(" | ");
  const travelers = event.notes?.match(/Travelers: ([^|]+)/)?.[1]?.trim() || "1";
  const budget = event.notes?.match(/Budget: \$?([^|]+)/)?.[1]?.trim() || "";
  const finalNotes = cleanNotes
    .split(" | ")
    .filter(part => !part.startsWith("Travelers: ") && !part.startsWith("Budget: "))
    .join(" | ");
  return {
    event_type: event.event_type || event.occasion_type || "trip",
    title: event.title || "",
    for_who: forceGroup ? "group" : (event.person_id ? "someone" : "myself"),
    person_id: event.person_id ? String(event.person_id) : "",
    group_id: "",
    event_date: event.event_date || event.occasion_date || event.next_occurrence || "",
    end_date: endDate,
    destination,
    trip_style: tripStyle,
    location: event.location || "",
    travelers,
    budget,
    notes: finalNotes,
  };
}

function RsvpPills({ inCount = 0, maybeCount = 0, outCount = 0, totalCount = 0 }) {
  if (totalCount > 0 && inCount === 0 && maybeCount === 0 && outCount === 0) {
    return (
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
        <span className="badge" style={{ background: "var(--cream)", color: "var(--ink-muted)", fontWeight: 600 }}>
          {totalCount} invited · no RSVPs yet
        </span>
      </div>
    );
  }
  const pills = [
    inCount > 0 ? { key: "in", label: `${inCount} coming`, bg: "var(--green-light)", color: "var(--green)" } : null,
    maybeCount > 0 ? { key: "maybe", label: `${maybeCount} maybe`, bg: "var(--gold-light)", color: "#8B6914" } : null,
    outCount > 0 ? { key: "out", label: `${outCount} out`, bg: "#FEE2E2", color: "#B42318" } : null,
  ].filter(Boolean);

  if (pills.length === 0) return null;

  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
      {pills.map(pill => (
        <span key={pill.key} className="badge" style={{ background: pill.bg, color: pill.color, fontWeight: 600 }}>
          {pill.label}
        </span>
      ))}
    </div>
  );
}

const PORTRAIT_TABLET_QUERY = "(min-width: 769px) and (max-width: 1024px) and (orientation: portrait)";
const COMPACT_EVENT_FORM_QUERY = `(max-width: 640px), ${PORTRAIT_TABLET_QUERY}`;
const COMPACT_EVENT_ACTIONS_QUERY = `(max-width: 767px), ${PORTRAIT_TABLET_QUERY}`;

function EventForm({ people, groups = [], currentUser, initial = null, onSave, onCancel, saving }) {
  const isCompactViewport = () => typeof window !== "undefined" && window.matchMedia(COMPACT_EVENT_FORM_QUERY).matches;
  const formRef = useRef(null);
  const [step, setStep] = useState(1);
  const [compactFlow, setCompactFlow] = useState(isCompactViewport);
  const [form, setForm] = useState({
    event_type:"trip", title:"", for_who:"myself", person_id:"", group_id:"",
    event_date:"", end_date:"", destination:"", trip_style:"", location:"", travelers:"1", budget:"", notes:"",
  });

  useEffect(() => {
    if (!initial) {
      setForm({
        event_type:"trip", title:"", for_who:"myself", person_id:"", group_id:"",
        event_date:"", end_date:"", destination:"", trip_style:"", location:"", travelers:"1", budget:"", notes:"",
      });
      setStep(1);
      return;
    }
    setForm({ travelers:"1", budget:"", ...initial });
    setStep(2);
  }, [initial]);

  useEffect(() => {
    const mq = window.matchMedia(COMPACT_EVENT_FORM_QUERY);
    const sync = () => setCompactFlow(mq.matches);
    sync();
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, []);

  const steppedFlow = compactFlow || isCompactViewport();

  useEffect(() => {
    if (!steppedFlow || !formRef.current) return undefined;
    const timer = window.setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 40);
    return () => window.clearTimeout(timer);
  }, [step, steppedFlow]);

  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));
  const selectedType = EVENT_TYPES.find(t=>t.value===form.event_type);
  const isTrip = selectedType?.hasTrip;
  const availableEventTypes = steppedFlow && form.event_type !== "baby_shower"
    ? EVENT_TYPES.filter(t => t.value !== "baby_shower")
    : EVENT_TYPES;
  const stepLabel = step === 1 ? "What type?" : step === 2 ? "Dates & details" : "Notes";
  const travelerCount = Math.max(1, Math.min(20, parseInt(form.travelers, 10) || 1));
  const eventTypeLabel = (label) => label
    .replace(" / ", " /\n")
    .replace("Birthday Party", "Birthday\nParty")
    .replace("Graduation Party", "Graduation")
    .replace("Holiday Gathering", "Holiday")
    .replace("Other Event", "Other")
    .replace("Baby Shower", "Baby\nShower");

  const handleTypeSelect = value => {
    setForm(f=>({...f,event_type:value}));
    if (steppedFlow) {
      window.setTimeout(() => setStep(2), 200);
    }
  };

  const setTravelers = delta => {
    setForm(f => {
      const next = Math.max(1, Math.min(20, (parseInt(f.travelers, 10) || 1) + delta));
      return { ...f, travelers: String(next) };
    });
  };

  const buildNotes = () => {
    const pieces = [];
    if (isTrip && form.travelers) pieces.push(`Travelers: ${travelerCount}`);
    if (form.budget) pieces.push(`Budget: $${String(form.budget).replace(/^\$/, "")}`);
    if (form.notes) pieces.push(form.notes);
    return pieces.join(" | ") || null;
  };

  const handleSubmit = (e, forceSubmit = false) => {
    e?.preventDefault?.();
    if (steppedFlow && step < 3 && !forceSubmit) {
      setStep(s => Math.min(3, s + 1));
      return;
    }

    // ── Group event path ──────────────────────────────────────────────
    if (form.for_who === "group") {
      if (!form.group_id) return;
      onSave({
        group_id: parseInt(form.group_id),
        event_type: form.event_type,
        title: form.title?.trim() || null,
        event_date: form.event_date,
        end_date: form.end_date || null,
        destination: form.destination || null,
        trip_style: form.trip_style || null,
        location: form.location || null,
        notes: buildNotes(),
      });
      return;
    }

    // ── Personal event path ───────────────────────────────────────────
    const personId = form.for_who === "someone"
      ? (parseInt(form.person_id, 10) || null)
      : null;

    onSave({
      person_id: personId || null,
      event_type: form.event_type,
      title: form.title?.trim() || null,
      event_date: form.event_date,
      end_date: form.end_date || null,
      destination: form.destination || null,
      trip_style: form.trip_style || null,
      location: form.location || null,
      notes: buildNotes(),
    });
  };

  // Audience options: always show Myself + Someone else; show Group only when groups exist
  const audienceOptions = [
    { value: "myself",  label: "Myself" },
    { value: "someone", label: "Someone else" },
    ...(groups.length > 0 ? [{ value: "group", label: "A group" }] : []),
  ];

  const TypeStep = (
    <div className="event-step-panel event-step-type">
      <div className="form-group">
        <label className="form-label">Event type *</label>
        <div className="event-type-grid">
          {availableEventTypes.map(t=>(
            <button key={t.value} type="button"
              onClick={()=>handleTypeSelect(t.value)}
              className={`event-type-tile${form.event_type===t.value ? " is-selected" : ""}`}>
              <div className="event-type-icon">{t.icon}</div>
              <span>{eventTypeLabel(t.label)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const DetailsStep = (
    <div className="event-step-panel">
      <div className="form-group">
        <label className="form-label">Who is this for?</label>
        <div className="event-audience-row">
          {audienceOptions.map(opt=>(
            <button key={opt.value} type="button"
              onClick={()=>setForm(f=>({...f,for_who:opt.value,person_id:"",group_id:""}))}
              className={`event-audience-pill${form.for_who===opt.value ? " is-selected" : ""}`}>{opt.label}</button>
          ))}
        </div>
        {/* Group planning available in Family plan */}
      </div>

      {form.for_who==="someone" && (
        <div className="form-group">
          <label className="form-label">Which person? *</label>
          <select className="form-select" value={form.person_id}
            onChange={set("person_id")} required>
            <option value="">Select a person...</option>
            {people.map(p=><option key={p.id} value={p.id}>{p.name} ({p.relationship})</option>)}
          </select>
        </div>
      )}

      {form.for_who==="group" && (
        <div className="form-group">
          <label className="form-label">Which group? *</label>
          <select className="form-select" value={form.group_id}
            onChange={set("group_id")} required>
            <option value="">Select a group...</option>
            {groups.map(g=><option key={g.id} value={g.id}>{g.emoji} {g.name}</option>)}
          </select>
        </div>
      )}

      <div className="grid-2 event-date-grid">
        <div className="form-group">
          <label className="form-label">{isTrip?"From":"Event date"} *</label>
          <input type="date" className="form-input" value={form.event_date} onChange={set("event_date")} required />
        </div>
        {isTrip && (
          <div className="form-group">
            <label className="form-label">To</label>
            <input type="date" className="form-input" value={form.end_date} onChange={set("end_date")} />
          </div>
        )}
      </div>

      {!isTrip && (
        <>
          <div className="form-group">
            <label className="form-label">Occasion title</label>
            <input
              className="form-input"
              value={form.title}
              onChange={set("title")}
              placeholder="Wedding anniversary, board game night..."
            />
          </div>
          <div className="form-group">
            <label className="form-label">Location</label>
            <input
              className="form-input"
              value={form.location}
              onChange={set("location")}
              placeholder="Home, restaurant, venue, city..."
            />
          </div>
        </>
      )}

      {isTrip && (
        <>
          <div className="form-group">
            <label className="form-label">Destination</label>
            <input className="form-input" value={form.destination} onChange={set("destination")}
              placeholder="Cancun, Paris, Hawaii, Las Vegas..." />
          </div>
          <div className="grid-2 event-trip-meta-grid">
            <div className="form-group">
              <label className="form-label">Travellers</label>
              <div className="event-counter">
                <button type="button" onClick={()=>setTravelers(-1)} aria-label="Decrease travellers">−</button>
                <strong>{travelerCount}</strong>
                <button type="button" onClick={()=>setTravelers(1)} aria-label="Increase travellers">+</button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Budget <span>(optional)</span></label>
              <div className="event-money-input">
                <span>$</span>
                <input inputMode="decimal" value={form.budget} onChange={e=>setForm(f=>({...f,budget:e.target.value.replace(/[^\d.]/g,"")}))} placeholder="0" />
              </div>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Trip style</label>
            <div className="event-trip-style-row">
              {TRIP_STYLES.map(s=>(
                <button key={s} type="button"
                  onClick={()=>setForm(f=>({...f,trip_style:f.trip_style===s?"":s}))}
                  className={`event-trip-chip${form.trip_style===s ? " is-selected" : ""}`}>{s}</button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );

  const NotesStep = (
    <div className="event-step-panel">
      <div className="form-group">
        <label className="form-label">{isTrip ? "Trip notes" : "Event vision / guest details"}</label>
        <textarea className="form-textarea" value={form.notes} onChange={set("notes")}
          placeholder={isTrip
            ? "All-inclusive resort, 4 adults 2 kids, budget, must-dos, anything to avoid..."
            : "Vibe, dress code, guest count, food ideas, budget, location notes, what matters most, anything to avoid..."}
          rows={3} />
        {!isTrip && (
          <div className="text-sm text-muted mt-8">
            This guides the AI event plan, invite details, timeline, food, supplies, and reminders.
          </div>
        )}
      </div>
    </div>
  );

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="event-add-form">
      <div className="event-form-header">
        <div>
          <h3>{initial ? "Edit event or trip" : "Add event or trip"}</h3>
          <p>Step {steppedFlow ? step : 1} of {steppedFlow ? 3 : 1} — {steppedFlow ? stepLabel : "Details"}</p>
        </div>
      </div>

      <div className="event-step-progress" aria-hidden="true">
        {[1,2,3].map(n => <span key={n} className={n === step ? "is-active" : n < step ? "is-done" : ""} />)}
        <em>{stepLabel}</em>
      </div>

      <div className="event-desktop-flow">
        {TypeStep}
        {DetailsStep}
        {NotesStep}
      </div>

      <div className="event-mobile-flow">
        {step === 1 && TypeStep}
        {step === 2 && DetailsStep}
        {step === 3 && NotesStep}
      </div>

      <div className="flex-center gap-8 mt-16 event-form-actions">
        {steppedFlow && step > 1 && (
          <button type="button" className="btn btn-secondary" onClick={()=>setStep(s=>Math.max(1,s-1))}>Back</button>
        )}
        {!steppedFlow && <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>}
        {steppedFlow && step < 3 ? (
          <>
            {step === 2 && (
              <button type="button" className="btn btn-secondary" onClick={(event) => { event.preventDefault(); event.stopPropagation(); handleSubmit(event, true); }} disabled={saving}>
                Save
              </button>
            )}
            <button type="button" className="btn btn-primary" onClick={(event)=>{ event.preventDefault(); event.stopPropagation(); setStep(s=>Math.min(3,s+1)); }}>
              {step === 1 ? "Next — Dates & details →" : "Add notes →"}
            </button>
          </>
        ) : (
        <button type={steppedFlow ? "button" : "submit"} onClick={steppedFlow ? handleSubmit : undefined} className="btn btn-primary" disabled={saving}>
          {saving
            ? "Saving..."
            : (initial?._forceGroup ? "Add to group" : (initial ? "Save changes" : "Add event"))}
        </button>
        )}
      </div>
    </form>
  );
}

// ── Personal event card ──────────────────────────────────────────────
function EventCard({ event, canShareFromHere, canGroupFromHere, onShareCopy, onPlan, onInvite, onEdit, onDelete, isPast = false, isFocused = false }) {
  const navigate = useNavigate();
  const [actionsOpen, setActionsOpen] = useState(false);
  const [compactActions, setCompactActions] = useState(false);
  const eventType = EVENT_TYPES.find(t=>t.value===event.occasion_type);
  const icon = eventType?.icon||"📅";
  const label = eventType?.label||event.occasion_type.replace(/_/g," ");
  const displayTitle = getEventDisplayTitle(event);
  const workflow = getEventWorkflow(event.occasion_type, event.event_workflow);
  const personalPrimaryAction = workflow.personal_primary_action;
  const isTrip = personalPrimaryAction === "trip" || event.occasion_type === "trip";
  const tripLabel = event.has_trip_plan ? "View trip plan" : (isPast ? "Open trip" : "Plan trip");
  const inviteLabel = event.share_slug ? "RSVPs" : "Invite";
  const eventLabel = event.has_event_plan ? "View event plan" : (isPast ? "Open event plan" : "Plan event");
  const primaryLabel = personalPrimaryAction === "trip"
    ? tripLabel
    : personalPrimaryAction === "add_to_group" && canGroupFromHere
        ? "Add to group"
        : eventLabel;
  const destination = event.destination || event.notes?.match(/Destination: ([^|]+)/)?.[1]?.trim();
  const tripStyle = event.trip_style || event.notes?.match(/Style: ([^|]+)/)?.[1]?.trim();
  const rawEventDate = event.event_date || event.next_occurrence || event.occasion_date || null;
  const eventDate = rawEventDate
    ? new Date(`${rawEventDate}T00:00:00`).toLocaleDateString("en-US",{weekday:"short",month:"long",day:"numeric"})
    : null;
  const detailItems = [destination, tripStyle].filter(Boolean);
  const daysLabel = event.days_until===0?"Today!":event.days_until===1?"Tomorrow":event.days_until!=null?`${event.days_until}d`:null;
  const mobileMeta = [eventDate, ...detailItems, daysLabel].filter(Boolean).join(" · ");
  const pantryMatchCount = event.event_plan_summary?.pantry_match_count || 0;
  const packingSummary = event.trip_plan_summary;
  const giftStatus = !isTrip && event.gift_status?.status ? event.gift_status : null;
  const isIncompleteTrip = isTrip && !isPast && displayTitle === "Trip";
  useEffect(() => {
    const query = window.matchMedia(COMPACT_EVENT_ACTIONS_QUERY);
    const update = () => {
      setCompactActions(query.matches);
      if (!query.matches) setActionsOpen(false);
    };
    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);
  const baseMenuItems = [
    personalPrimaryAction !== "trip" && personalPrimaryAction !== "open_event"
      ? { key: "plan", label: eventLabel, onClick: () => navigate(buildPersonalEventPlanUrl(event)) }
      : null,
    { key: "edit", label: "Edit event", onClick: () => onEdit(event) },
    canShareFromHere ? { key: "share-copy", label: "Share with Family", onClick: () => onShareCopy(event) } : null,
    { key: "remove", label: "Remove event", onClick: () => onDelete(event.id), danger: true },
  ];
  const handlePrimaryAction = () => {
    if (personalPrimaryAction === "add_to_group" && canGroupFromHere) {
      onEdit({ ...event, _forceGroup: true });
      return;
    }
    onPlan(event);
  };
  const sheetActions = [
    primaryLabel ? {
      key: "primary",
      icon: isTrip ? "✈" : "🧭",
      label: primaryLabel,
      detail: isTrip ? "Itinerary, packing list & timeline" : "AI plan, prep tasks & invites",
      onClick: handlePrimaryAction,
    } : null,
    !isTrip ? {
      key: "rsvps",
      icon: "👥",
      label: inviteLabel,
      detail: event.rsvp_count > 0 ? `${event.rsvp_in || 0} coming · ${event.rsvp_maybe || 0} maybe · ${event.rsvp_out || 0} out` : "Invite guests and view responses",
      onClick: () => onInvite(event),
    } : null,
    giftStatus ? {
      key: "gifts",
      icon: "🎁",
      label: giftStatus.label,
      detail: "Open People & Gifts",
      onClick: () => navigate(giftStatus.action_url || "/app/people"),
    } : null,
    {
      key: "edit",
      icon: "✎",
      label: "Edit event",
      detail: "Update date, details, and notes",
      onClick: () => onEdit(event),
    },
    canShareFromHere ? {
      key: "share-copy",
      icon: "👥",
      label: "Share with Family",
      detail: "Share a copy with your Family plan",
      onClick: () => onShareCopy(event),
    } : null,
    {
      key: "remove",
      icon: "🗑",
      label: "Remove event",
      detail: "Delete this event from your list",
      onClick: () => onDelete(event.id),
      danger: true,
    },
  ];

  return (
    <div
      className={`card card-sm event-card ${isTrip ? "is-trip-card" : "is-occasion-card"} ${eventEdgeClass({ days: event.days_until, isPast, isReady: isTrip ? event.has_trip_plan : event.has_event_plan })}${isFocused ? " is-focused" : ""}`}
      data-personal-event-id={event.id}
      onClick={() => {
        if (compactActions) setActionsOpen(true);
      }}
      role={compactActions ? "button" : undefined}
      tabIndex={compactActions ? 0 : undefined}
      onKeyDown={e => {
        if (!compactActions) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setActionsOpen(true);
        }
      }}
    >
      <div className="event-card-icon" style={{
        width:48,height:48,borderRadius:"var(--radius-sm)",flexShrink:0,
        background:"var(--cream)",display:"flex",alignItems:"center",
        justifyContent:"center",fontSize:22,border:"1px solid var(--border)",
      }}>{icon}</div>

      <div className="event-card-summary">
        <div className="content-title-row" style={{flexWrap:"nowrap"}}>
          <span style={{fontWeight:500,fontSize:14,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",minWidth:0}}>{displayTitle}</span>
          {/* mobile inline context */}
        </div>
        <div className="event-desktop-rows">
          {eventDate ? <div className="text-sm text-muted">{label} · {eventDate}</div> : null}
          {detailItems.length > 0 ? (
            <div className="event-card-meta">
              {detailItems.map(item => (
                <span key={item} className="content-meta-chip">{item}</span>
              ))}
              {isTrip && packingSummary?.total_items > 0 ? (
                <span className="content-meta-chip events-card-pack-chip">{packingSummary.percent}% packed</span>
              ) : null}
            </div>
          ) : null}
        </div>
        {mobileMeta ? <div className="event-mobile-meta text-sm text-muted">{mobileMeta}</div> : null}
        {!isTrip && event.rsvp_count > 0 ? (
          <div className="event-card-rsvp-row" style={{ marginTop: 8 }}>
            <RsvpPills inCount={event.rsvp_in} maybeCount={event.rsvp_maybe} outCount={event.rsvp_out} totalCount={event.rsvp_count} />
          </div>
        ) : null}
        {isIncompleteTrip ? (
          <button
            type="button"
            className="events-card-incomplete-chip"
            onClick={e => {
              e.stopPropagation();
              navigate(`${buildPersonalTripUrl(event)}&tab=details`);
            }}
          >
            Incomplete · tap to finish
          </button>
        ) : null}
        {!isTrip && pantryMatchCount > 0 ? (
          <div className="events-card-callout">
            <span>Food</span>
            {pantryMatchCount} pantry item{pantryMatchCount !== 1 ? "s" : ""} ready to use
          </div>
        ) : null}
      </div>

      <div className="event-card-side">
        {giftStatus ? (
          <button
            type="button"
            className={`events-card-gift-chip is-${giftStatus.status}`}
            onClick={e => {
              e.stopPropagation();
              navigate(giftStatus.action_url || "/app/people");
            }}
          >
            {giftStatus.label}
          </button>
        ) : null}
        {event.days_until!==null && event.days_until!==undefined && (
          <span className={`days-pill event-desktop-days ${event.days_until<=14?"days-urgent":event.days_until<=30?"days-soon":"days-ok"}`}>
            {event.days_until===0?"Today!":event.days_until===1?"Tomorrow":`${event.days_until}d`}
          </span>
        )}

        <div className="flex-center gap-8 event-card-actions desktop-inline-actions" onClick={e => e.stopPropagation()}>
          {primaryLabel ? (
          <button type="button" className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); handlePrimaryAction(); }}>
            {primaryLabel}
          </button>
        ) : null}
        {!isTrip ? (
            <button type="button" className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); onInvite(event); }}>
              {inviteLabel}
            </button>
          ) : null}
          <ActionMenu
            label="⋯"
            items={baseMenuItems}
          />
        </div>
        <div className={`compact-actions-row mobile-compact-actions${primaryLabel ? " has-primary" : " menu-only"}`}>
          <span className="event-card-mobile-chevron">›</span>
        </div>
      </div>
      {compactActions && actionsOpen && (
        <EventActionSheet
          title={displayTitle}
          subtitle={mobileMeta || eventDate || "Event actions"}
          actions={sheetActions}
          onClose={() => setActionsOpen(false)}
        />
      )}
    </div>
  );
}

// ── Group event card ─────────────────────────────────────────────────
function GroupEventCard({ event, onNavigate, onOpenRegistry, isPast = false, isFocused = false }) {
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);

  const eventType = EVENT_TYPES.find(t => t.value === event.event_type);
  const icon = eventType?.icon || "📅";
  const label = eventType?.label || event.event_type.replace(/_/g, " ");
  const workflow = getEventWorkflow(event.event_type, event.event_workflow);
  const isTrip = workflow.supports_trip_plan;
  const tripLabel = event.has_trip_plan ? "View trip plan" : (isPast ? "Open trip" : "Plan trip");
  const eventPlanLabel = event.has_event_plan ? "View event plan" : (isPast ? "Open event plan" : "Plan event");
  const shareLabel = sharing ? "Sharing..." : copied ? "Copied!" : "Share event";
  const primaryLabel = workflow.group_primary_action === "trip" ? tripLabel : shareLabel;

  const dateStr = new Date(event.event_date + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "short", month: "long", day: "numeric",
  });
  const endStr = event.end_date
    ? new Date(event.end_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : null;

  const today = new Date(); today.setHours(0,0,0,0);
  const evDate = new Date(event.event_date + "T00:00:00");
  const daysUntil = Math.round((evDate - today) / (1000 * 60 * 60 * 24));

  const daysLabel = daysUntil===0?"Today!":daysUntil===1?"Tomorrow":daysUntil>=0?`${daysUntil}d`:null;
  const mobileMeta = [dateStr, endStr?`→ ${endStr}`:null, daysLabel, event.destination].filter(Boolean).join(" · ");

  const openEventHref = `/app/events?group_event_id=${event.id}`;

  const handleShare = async () => {
    if (event.share_slug) {
      const url = `${window.location.origin}/event/${event.share_slug}`;
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
      return;
    }
    setSharing(true);
    try {
      const data = await eventsApi.generateShareLink(event.id);
      const url = `${window.location.origin}${data.share_url}`;
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
      event.share_slug = data.share_slug; // update in place for next click
    } finally {
      setSharing(false);
    }
  };

  return (
    <div
      className={`card card-sm event-card ${isTrip ? "is-trip-card" : "is-occasion-card"} ${eventEdgeClass({ days: daysUntil, isPast, isReady: isTrip ? event.has_trip_plan : event.has_event_plan })}${isFocused ? " is-focused" : ""}`}
      data-group-event-id={event.id}
    >
      <div className="event-card-icon" style={{
        width:48,height:48,borderRadius:"var(--radius-sm)",flexShrink:0,
        background:"var(--cream)",display:"flex",alignItems:"center",
        justifyContent:"center",fontSize:22,border:"1px solid var(--border)",
      }}>{icon}</div>

      <div className="event-card-summary">
        <div className="content-title-row" style={{flexWrap:"nowrap"}}>
          <span style={{fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",minWidth:0}}>
            {event.title || label}
          </span>
          {event.destination && <span className="content-meta-chip event-desktop-chip">📍 {event.destination}</span>}
          <span className="event-desktop-chip" style={{fontSize:11,fontWeight:500,padding:"2px 7px",background:"var(--green-light)",color:"var(--green)",borderRadius:20,border:"1px solid var(--border)",flexShrink:0}}>
            {event.group_emoji} {event.group_name}
          </span>
          <span className="event-mobile-name"> · {event.group_name}</span>
        </div>
        <div className="event-desktop-rows">
          <div className="text-sm text-muted">
            {dateStr}{endStr ? ` → ${endStr}` : ""}
          </div>
          {!isTrip && event.rsvp_count > 0 ? <RsvpPills inCount={event.rsvp_in} maybeCount={event.rsvp_maybe} outCount={event.rsvp_out} totalCount={event.rsvp_count} /> : null}
        </div>
        {mobileMeta ? <div className="event-mobile-meta text-sm text-muted">{mobileMeta}</div> : null}
      </div>

      <div className="event-card-side">
        {daysUntil >= 0 && (
          <span className={`days-pill event-desktop-days ${daysUntil<=14?"days-urgent":daysUntil<=30?"days-soon":"days-ok"}`}>
            {daysUntil===0?"Today!":daysUntil===1?"Tomorrow":`${daysUntil}d`}
          </span>
        )}

        <div className="flex-center gap-8 event-card-actions desktop-inline-actions">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => (
              isTrip
                ? onNavigate(buildGroupTripUrl(event))
                : handleShare()
            )}
          >
            {primaryLabel}
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => onNavigate(isTrip ? openEventHref : buildGroupEventPlanUrl(event))}
          >
            {isTrip ? "Open event" : eventPlanLabel}
          </button>
          {workflow.supports_registry ? (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => onOpenRegistry(event)}
            >
              Open registry
            </button>
          ) : null}
        </div>
        <div className="compact-actions-row mobile-compact-actions">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => (
              isTrip
                ? onNavigate(buildGroupTripUrl(event))
                : handleShare()
            )}
          >
            {primaryLabel}
          </button>
          <ActionMenu
            label="⋯"
            items={[
              { key: "plan", label: eventPlanLabel, onClick: () => onNavigate(isTrip ? openEventHref : buildGroupEventPlanUrl(event)) },
              { key: "open", label: "Open event", onClick: () => onNavigate(openEventHref) },
              workflow.supports_registry ? { key: "registry", label: "Open registry", onClick: () => onOpenRegistry(event) } : null,
              !isTrip ? { key: "share", label: shareLabel, onClick: handleShare } : null,
            ].filter(Boolean)}
          />
        </div>
      </div>
    </div>
  );
}

function EventsHeroStats({ upcomingCount, nextEvent, progressLabel, tasksDue, rsvpsPending }) {
  const nextLabel = nextEvent
    ? (nextEvent._daysUntil === 0 ? "today" : nextEvent._daysUntil === 1 ? "tomorrow" : `in ${nextEvent._daysUntil} days`)
    : "none scheduled";
  return (
    <div className="events-hero-stats">
      <div>
        <div className="events-hero-eyebrow">Events & Trips</div>
        <div className="events-hero-number">{upcomingCount} upcoming</div>
        <div className="events-hero-sub">
          {nextEvent ? `${getEventDisplayTitle(nextEvent)} · next ${nextLabel}` : "No upcoming plans yet"}
        </div>
      </div>
      <div className="events-hero-pill-row">
        {progressLabel ? <StatPill>{progressLabel}</StatPill> : null}
        {tasksDue > 0 ? <StatPill tone="warn">{tasksDue} task{tasksDue !== 1 ? "s" : ""} due</StatPill> : null}
        {rsvpsPending > 0 ? <StatPill>{rsvpsPending} RSVP{rsvpsPending !== 1 ? "s" : ""} pending</StatPill> : null}
      </div>
    </div>
  );
}

function NextUpCard({ event, onOpen, tripPantryUseUpCount = 0 }) {
  const [expanded, setExpanded] = useState(() => {
    try {
      const stored = window.localStorage.getItem("next_up_expanded");
      // Default open unless user has explicitly collapsed it
      return stored === null ? true : stored === "true";
    } catch {
      return true;
    }
  });
  if (!event) return null;
  const isTrip = (event.occasion_type || event.event_type) === "trip";
  const packing = event.trip_plan_summary || {};
  const eventPlan = event.event_plan_summary || {};
  const percent = isTrip ? (packing.percent || 0) : 0;
  const packedText = packing.total_items ? `${packing.packed_count}/${packing.total_items}` : "Not started";
  const prepTaskCount = eventPlan.task_count || 0;
  const prepOpenCount = eventPlan.open_task_count || 0;
  const prepDoneCount = Math.max(0, prepTaskCount - prepOpenCount);
  const dayText = packing.day_count || (event.end_date && event.event_date
    ? Math.max(1, Math.round((new Date(`${event.end_date}T00:00:00`) - new Date(`${event.event_date}T00:00:00`)) / (1000 * 60 * 60 * 24)))
    : 1);
  const noteBudget = event.notes?.match(/Budget: \$?([^|]+)/)?.[1]?.trim();
  const budget = packing.budget_range || (noteBudget ? `$${noteBudget}` : null);
  const daysLabel = event._daysUntil === 0 ? "Today" : event._daysUntil === 1 ? "Tomorrow" : `${event._daysUntil} days`;
  const eventPrimaryActionLabel = event.has_event_plan ? "✦ View event plan ↗" : "✦ Plan this event ↗";
  const handleDrawerAction = (clickEvent, tab) => {
    clickEvent.stopPropagation();
    onOpen(event, tab);
  };
  const toggleExpanded = () => {
    setExpanded((current) => {
      const next = !current;
      try {
        window.localStorage.setItem("next_up_expanded", String(next));
      } catch {
        // Ignore storage failures; the drawer still works for this session.
      }
      return next;
    });
  };

  return (
    <section className="events-next-up">
      <div className="events-next-kicker">📆 Next up</div>
      <div
        className={`events-next-card${expanded ? " is-expanded" : " is-collapsed"}`}
      >
        <button
          type="button"
          className="events-next-head"
          onClick={toggleExpanded}
          aria-expanded={expanded}
        >
          <div className="events-next-icon">{isTrip ? "✈" : EVENT_TYPES.find(t => t.value === event.occasion_type)?.icon || "📅"}</div>
          <div>
            <h3>{getEventDisplayTitle(event)}</h3>
            <p>{formatShortDate(event.event_date)}{event.end_date ? `-${formatShortDate(event.end_date).replace(/[A-Za-z]+ /, "")}` : ""} · {event.destination || event.location || event.person_name || "Details pending"}</p>
          </div>
          <span>{daysLabel}</span>
          <i className="events-next-chevron">›</i>
        </button>
        {expanded ? (
          <>
            {isTrip ? (
              <div className="events-next-progress">
                <div><strong>Packing</strong><em>{percent}%</em></div>
                <div className="events-next-bar"><i style={{ width: `${Math.max(0, Math.min(percent, 100))}%` }} /></div>
              </div>
            ) : null}
            <div className="events-next-stats">
              {isTrip ? (
                <div><strong>{packedText}</strong><span>Packed</span></div>
              ) : prepTaskCount > 0 ? (
                <div><strong>{prepDoneCount}/{prepTaskCount}</strong><span>Prep done</span></div>
              ) : null}
              <div><strong>{dayText}</strong><span>{isTrip ? "Days planned" : "Event day"}</span></div>
              {!isTrip ? <div><strong>{daysLabel}</strong><span>Days until</span></div> : null}
              {!isTrip && prepTaskCount > 0 ? (
                <div><strong>{prepOpenCount > 0 ? prepOpenCount : "Ready"}</strong><span>{prepOpenCount === 1 ? "Task due" : prepOpenCount > 0 ? "Tasks due" : "No tasks due"}</span></div>
              ) : budget ? (
                <div><strong>{budget}</strong><span>Budget</span></div>
              ) : null}
            </div>
            <div className="events-next-callouts">
              {isTrip && tripPantryUseUpCount > 0 ? <div><i>🥗</i><strong>Use {tripPantryUseUpCount} pantry item{tripPantryUseUpCount !== 1 ? "s" : ""} before leaving</strong><span>Food</span></div> : null}
              {isTrip ? <div><i>↻</i><strong>Review pauseable subscriptions</strong><span>Subscriptions</span></div> : null}
              {!isTrip && event.rsvp_count > 0 ? <div><i>☑</i><strong>{event.rsvp_in || 0} coming · {event.rsvp_maybe || 0} maybe</strong><span>RSVPs</span></div> : null}
            </div>
            <div className="events-next-actions">
              <button type="button" className="is-primary" onClick={(clickEvent) => handleDrawerAction(clickEvent, "plan")}>
                {isTrip ? "✦ View itinerary ↗" : eventPrimaryActionLabel}
              </button>
              <button type="button" onClick={(clickEvent) => handleDrawerAction(clickEvent, isTrip ? "packing" : "prep")}>
                {isTrip ? "🧳 Open packing list ↗" : "📋 View prep tasks ↗"}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}

function EventsAskBar({ nextEvent, upcomingCount, pastCount }) {
  const prompts = ["What should I plan next?", "What needs attention?", "Summarize my trips"];

  function openEventsAsk(question = "") {
    const clean = String(question || "").trim();
    const nextTitle = nextEvent ? getEventDisplayTitle(nextEvent) : "";
    const contextParts = [
      `${upcomingCount} upcoming`,
      `${pastCount} past`,
      nextTitle ? `Next: ${nextTitle}` : null,
      nextEvent?.event_date ? `Next date: ${nextEvent.event_date}` : null,
      nextEvent?.destination ? `Destination: ${nextEvent.destination}` : null,
    ].filter(Boolean).join(" | ");
    window.dispatchEvent(new CustomEvent("hm_open_ask", {
      detail: {
        question: clean,
        context: {
          module: "events",
          module_label: "Events & Trips",
          surface: "Events landing",
          summary: contextParts,
          upcoming_count: upcomingCount,
          past_count: pastCount,
          next_event_title: nextTitle,
          next_event_date: nextEvent?.event_date || "",
          destination: nextEvent?.destination || "",
        },
      },
    }));
  }

  return (
    <div className="fd-food-askbar hm-events-askbar">
      <input
        className="fd-food-askbar__input"
        placeholder="✦ Ask about events & trips..."
        onFocus={() => openEventsAsk("")}
        readOnly
      />
      <div className="fd-food-askbar__chips">
        {prompts.map(prompt => (
          <button key={prompt} type="button" className="fd-food-askbar__chip" onClick={() => openEventsAsk(prompt)}>
            {prompt}
          </button>
        ))}
      </div>
      <button type="button" className="fd-food-askbar__button" onClick={() => openEventsAsk(prompts[0])}>
        ✦ Ask
      </button>
    </div>
  );
}

function ConfirmModal({ title, message, warning, confirmLabel, onClose, onConfirm, className = "", secondaryAction = null }) {
  return (
    <BottomSheet title={title} label={title} onClose={onClose} bodyClassName={`event-confirm-sheet-body ${className}`.trim()}>
      {warning && (
        <div className="notice-banner is-warn mb-16">
          <p>{warning}</p>
        </div>
      )}
      {typeof message === "string"
        ? <p className="text-muted mb-16">{message}</p>
        : <div className="mb-16">{message}</div>}
      <div className={`event-confirm-sheet-actions${secondaryAction ? " has-secondary-action" : ""}`}>
        <button type="button" className="btn btn-primary" onClick={onConfirm}>{confirmLabel}</button>
        {secondaryAction ? (
          <button type="button" className="btn btn-secondary" onClick={secondaryAction.onClick}>
            {secondaryAction.label}
          </button>
        ) : null}
        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
      </div>
    </BottomSheet>
  );
}

function FormPresentation({ title, onClose, children }) {
  const presentation = useFormPresentation();

  if (presentation === "modal") {
    return (
      <CenteredModal title={title} onClose={onClose}>
        {children}
      </CenteredModal>
    );
  }

  return (
    <BottomSheet title={title} label={title} onClose={onClose} bodyClassName="event-form-sheet-body">
      {children}
    </BottomSheet>
  );
}

export default function EventsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const toast = useToast();
  const [personalEvents, setPersonalEvents] = useState([]);
  const [groupEvents, setGroupEvents] = useState([]);
  const [people, setPeople] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingEventId, setDeletingEventId] = useState(null);
  const [deleteNoticeMessage, setDeleteNoticeMessage] = useState("");
  const [shareEvent, setShareEvent] = useState(null);
  const [shareTargetId, setShareTargetId] = useState("");
  const [shareMembers, setShareMembers] = useState([]);
  const [shareSaving, setShareSaving] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [shareSuccess, setShareSuccess] = useState(null);
  const [inviteEvent, setInviteEvent] = useState(null);
  const [inviteUrl, setInviteUrl] = useState("");
  const [rsvpDetails, setRsvpDetails] = useState(null);
  const [rsvpFilter, setRsvpFilter] = useState("all");
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [copyingInvite, setCopyingInvite] = useState(false);
  const [focusedCardKey, setFocusedCardKey] = useState("");
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [pantryItems, setPantryItems] = useState([]);
  const showEventActionWorking = useMinLoad(saving || shareSaving || copyingInvite, 700);

  useOnEscape(useCallback(() => {
    if (inviteEvent) { setInviteEvent(null); setInviteUrl(""); setRsvpDetails(null); return; }
    if (shareSuccess) { setShareSuccess(null); return; }
    if (shareEvent) { setShareEvent(null); setShareError(null); return; }
    if (confirmDeleteId) { setConfirmDeleteId(null); return; }
    if (showForm) {
      setShowForm(false);
      setEditingEvent(null);
    }
  }, [inviteEvent, shareSuccess, shareEvent, confirmDeleteId, showForm]));

  useEffect(() => {
    const editEventId = location.state?.editEventId;
    if (!editEventId) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("edit_event_id", String(editEventId));
    setSearchParams(nextParams, { replace: true });
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate, searchParams, setSearchParams]);

  useEffect(() => {
    const editEventId = Number(searchParams.get("edit_event_id")) || null;
    if (!editEventId || personalEvents.length === 0) return;

    const match = personalEvents.find(event => event.id === editEventId);
    if (!match) return;

    setEditingEvent(match);
    setShowForm(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("edit_event_id");
    setSearchParams(nextParams, { replace: true });
  }, [personalEvents, searchParams, setSearchParams]);

  useEffect(() => {
    const personalEventId = Number(searchParams.get("event_id")) || null;
    if (!personalEventId || personalEvents.length === 0 || showForm) return;
    const match = document.querySelector(`[data-personal-event-id="${personalEventId}"]`);
    if (!match) return;
    setFocusedCardKey(`personal-${personalEventId}`);
    match.scrollIntoView({ behavior: "smooth", block: "center" });
    const clearParamsTimer = window.setTimeout(() => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("event_id");
      setSearchParams(nextParams, { replace: true });
    }, 250);
    const clearFocusTimer = window.setTimeout(() => setFocusedCardKey(""), 2200);
    return () => {
      window.clearTimeout(clearParamsTimer);
      window.clearTimeout(clearFocusTimer);
    };
  }, [personalEvents, searchParams, setSearchParams, showForm]);

  useEffect(() => {
    const groupEventId = Number(searchParams.get("group_event_id")) || null;
    if (!groupEventId || groupEvents.length === 0 || showForm) return;
    const match = document.querySelector(`[data-group-event-id="${groupEventId}"]`);
    if (!match) return;
    setFocusedCardKey(`group-${groupEventId}`);
    match.scrollIntoView({ behavior: "smooth", block: "center" });
    const clearParamsTimer = window.setTimeout(() => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("group_event_id");
      setSearchParams(nextParams, { replace: true });
    }, 250);
    const clearFocusTimer = window.setTimeout(() => setFocusedCardKey(""), 2200);
    return () => {
      window.clearTimeout(clearParamsTimer);
      window.clearTimeout(clearFocusTimer);
    };
  }, [groupEvents, searchParams, setSearchParams, showForm]);

  const currentWorkspace = user?.workspaces?.find(workspace => workspace.id === user?.active_workspace_id) || user?.workspace || null;
  const hasFamilyAccess = user?.plan === "family" || (user?.workspaces || []).some(workspace => workspace.plan === "family");
  const canShareFromHere = hasFamilyAccess && (currentWorkspace?.workspace_type || "personal") === "personal";
  const canGroupFromHere = groups.length > 0;

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    Promise.all([
      eventsApi.listPersonal(),
      peopleApi.listCompact(),
      groupsApi.allEvents().catch(() => []),
      groupsApi.list().catch(() => []),
      pantryApi.list().catch(() => []),
    ]).then(([personalEvs, ppl, grpEvents, grps, pantry]) => {
      if (!ignore) {
        setPersonalEvents(personalEvs || []);
        setPeople(ppl);
        setGroupEvents(grpEvents || []);
        setGroups(grps || []);
        setPantryItems(pantry || []);
      }
    }).finally(() => {
      if (!ignore) setLoading(false);
    });
    return () => { ignore = true; };
  }, [user?.active_workspace_id]);

  const handleSave = async data => {
    setSaving(true); setError(null);
    try {
      // ── Group event ───────────────────────────────────────────────────
      if (data.group_id) {
        const { group_id, ...eventData } = data;
        const savedGroupEvent = await groupsApi.createEvent(group_id, eventData);
        if (editingEvent?._forceGroup) {
          await eventsApi.removePersonal(editingEvent.id);
        }
        const grpEvents = await groupsApi.allEvents().catch(() => []);
        const personalEvs = await eventsApi.listPersonal();
        setGroupEvents(mergeEventById(grpEvents || [], savedGroupEvent));
        setPersonalEvents(personalEvs || []);
        setShowForm(false);
        setEditingEvent(null);
        toast?.("Event added to group", "success");
        return;
      }
      // ── Personal event ────────────────────────────────────────────────
      let savedPersonalEvent = null;
      if (editingEvent) {
        savedPersonalEvent = await eventsApi.updatePersonal(editingEvent.id, data);
      } else {
        savedPersonalEvent = await eventsApi.createPersonal(data);
      }
      const personalEvs = await eventsApi.listPersonal();
      setPersonalEvents(mergeEventById(personalEvs || [], savedPersonalEvent));
      setShowForm(false);
      setEditingEvent(null);
      toast?.(editingEvent ? "Event updated" : "Event added", "success");
    } catch(e) {
      setError(e.message);
      toast?.(e.message, "error");
    } finally { setSaving(false); }
  };

  const handleDelete = async id => {
    setDeletingEventId(id);
    try {
      await eventsApi.removePersonal(id);
      setPersonalEvents(ev => ev.filter(e => e.id !== id));
      toast?.("Event removed", "info");
    } catch (e) {
      setError(e.message || "Could not remove event");
      toast?.(e.message || "Could not remove event", "error");
    } finally {
      setDeletingEventId(null);
      setDeleteNoticeMessage("");
    }
  };

  const handlePlan = async (event) => {
    const workflow = getEventWorkflow(event.occasion_type, event.event_workflow);
    if (workflow.personal_primary_action === "trip") {
      navigate(buildPersonalTripUrl(event));
      return;
    }
    navigate(buildPersonalEventPlanUrl(event));
  };

  const handleInvite = async (event) => {
    setRsvpFilter("all");
    if (event.share_slug) {
      setInviteEvent(event);
      setInviteUrl(`${window.location.origin}/event/${event.share_slug}`);
      setRsvpLoading(true);
      try {
        const data = await eventsApi.listPersonalRsvps(event.id);
        setRsvpDetails(data);
      } catch (e) {
        toast?.(e.message || "Could not load RSVP responses", "error");
      } finally {
        setRsvpLoading(false);
      }
      return;
    }
    setSaving(true);
    try {
      const data = await eventsApi.generatePersonalShareLink(event.id);
      const url = `${window.location.origin}${data.share_url}`;
      await navigator.clipboard.writeText(url);
      setPersonalEvents(events => events.map(item => (
        item.id === event.id ? { ...item, share_slug: data.share_slug } : item
      )));
      setInviteEvent({ ...event, share_slug: data.share_slug });
      setInviteUrl(url);
      setRsvpDetails({ ...event, share_slug: data.share_slug, rsvps: [], rsvp_count: 0, rsvp_in: 0, rsvp_maybe: 0, rsvp_out: 0 });
      toast?.("RSVP invite link copied", "success");
    } catch (e) {
      toast?.(e.message || "Could not create RSVP invite", "error");
    } finally {
      setSaving(false);
    }
  };

  const copyInviteLink = async () => {
    if (!inviteUrl) return;
    setCopyingInvite(true);
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast?.("RSVP invite link copied", "success");
    } catch (e) {
      toast?.("Could not copy invite link", "error");
    } finally {
      setCopyingInvite(false);
    }
  };

  useEffect(() => {
    const slug = searchParams.get("rsvp_slug");
    if (!slug || showForm) return;
    const personalMatch = personalEvents.find(event => event.share_slug === slug);
    if (personalMatch) {
      handleInvite(personalMatch);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("rsvp_slug");
      setSearchParams(nextParams, { replace: true });
      return;
    }
    const groupMatch = groupEvents.find(event => event.share_slug === slug);
    if (groupMatch) {
      setFocusedCardKey(`group-${groupMatch.id}`);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("rsvp_slug");
      nextParams.set("group_event_id", String(groupMatch.id));
      setSearchParams(nextParams, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupEvents, personalEvents, searchParams, setSearchParams, showForm]);

  const rsvpRows = rsvpDetails?.rsvps || [];
  const filteredRsvps = rsvpFilter === "all" ? rsvpRows : rsvpRows.filter(rsvp => rsvp.status === rsvpFilter);
  const rsvpTabs = [
    { key: "all", label: "All", count: rsvpRows.length },
    { key: "in", label: "Coming", count: rsvpRows.filter(rsvp => rsvp.status === "in").length },
    { key: "maybe", label: "Maybe", count: rsvpRows.filter(rsvp => rsvp.status === "maybe").length },
    { key: "out", label: "Can't make it", count: rsvpRows.filter(rsvp => rsvp.status === "out").length },
  ];

  const openEventRegistry = event => {
    const label =
      event.title ||
      event.destination ||
      EVENT_TYPES.find(type => type.value === event.event_type)?.label ||
      "Event registry";
    navigate(`/app/people?context=event&group_event_id=${event.id}&title=${encodeURIComponent(label)}`);
  };

  const openShare = async event => {
    setShareEvent(event);
    setShareTargetId("");
    setShareMembers([]);
    setShareError(null);
    try {
      const data = await sharingApi.members();
      const members = Array.isArray(data.members) ? data.members : [];
      setShareMembers(members);
      setShareTargetId(members[0]?.user_id ? String(members[0].user_id) : "");
      if (members.length === 0) {
        setShareError("Add another Family member in Settings before sharing.");
      }
    } catch (e) {
      setShareError(e.message);
    }
  };

  const handleShare = async () => {
    if (!shareEvent || !shareTargetId) return;
    setShareSaving(true); setShareError(null);
    try {
      const targetMember = shareMembers.find(member => String(member.user_id) === String(shareTargetId));
      await sharingApi.create({
        resource_type: (shareEvent.event_type || shareEvent.occasion_type) === "trip" ? "event" : "event",
        resource_id: shareEvent.id,
        recipient_user_id: Number(shareTargetId),
      });
      setShareEvent(null);
      setShareSuccess({
        title: "Shared",
        message: `${shareEvent.title || shareEvent.destination || "Event"} is now visible in Shared with me for ${targetMember?.full_name || "that Family member"}. Your private account stays separate.`,
      });
    } catch (e) {
      setShareError(e.message);
    } finally { setShareSaving(false); }
  };

  // ── Build grouped sections ─────────────────────────────────────────
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const normalizedPersonal = personalEvents.map(e => {
    const rawDate = e.event_date || e.occasion_date || e.next_occurrence;
    const baseDate = rawDate ? new Date(rawDate + "T00:00:00") : new Date("9999-01-01");
    const daysUntil = rawDate ? Math.round((baseDate - today) / (1000 * 60 * 60 * 24)) : null;
    const isPast = daysUntil !== null && daysUntil < 0;
    return {
      _type: "personal",
      _sortDate: baseDate,
      _daysUntil: daysUntil,
      _isPast: isPast,
      // normalise type field — PersonalEvent uses event_type; expose occasion_type alias for EventCard
      occasion_type: e.event_type || e.occasion_type,
      ...e,
      days_until: daysUntil,
    };
  });

  const normalizedGroup = groupEvents.map(e => {
    const evDate = new Date(e.event_date + "T00:00:00");
    const daysUntil = Math.round((evDate - today) / (1000 * 60 * 60 * 24));
    return {
      _type: "group",
      _sortDate: evDate,
      _daysUntil: daysUntil,
      _isPast: daysUntil < 0,
      ...e,
    };
  });

  const sortEvents = (items) => [...items].sort((a, b) => a._sortDate - b._sortDate);
  const buildSections = (items, phase) => {
    const phaseItems = items.filter(event => phase === "past" ? event._isPast : !event._isPast);
    return [
      { key: `${phase}-group-trips`, title: phase === "past" ? "Earlier shared trips" : "Group trips", items: sortEvents(phaseItems.filter(event => event._type === "group" && event.event_type === "trip")) },
      { key: `${phase}-group-events`, title: phase === "past" ? "Earlier shared events" : "Group events", items: sortEvents(phaseItems.filter(event => event._type === "group" && event.event_type !== "trip")) },
      { key: `${phase}-personal-trips`, title: phase === "past" ? "Earlier personal trips" : "Personal trips", items: sortEvents(phaseItems.filter(event => event._type === "personal" && event.occasion_type === "trip")) },
      { key: `${phase}-personal-events`, title: phase === "past" ? "Earlier personal occasions" : "Personal occasions", items: sortEvents(phaseItems.filter(event => event._type === "personal" && event.occasion_type !== "trip")) },
    ].filter(section => section.items.length > 0);
  };
  const allEvents = [...normalizedGroup, ...normalizedPersonal];
  const upcomingSections = buildSections(allEvents, "upcoming");
  const pastSections = buildSections(allEvents, "past");
  const totalCount = normalizedPersonal.length + normalizedGroup.length;
  const upcomingEvents = sortEvents(allEvents.filter(event => !event._isPast));
  const pastEvents = sortEvents(allEvents.filter(event => event._isPast));
  const upcomingTripCount = upcomingEvents.filter(event => (event.occasion_type || event.event_type) === "trip").length;
  const upcomingOccasionCount = Math.max(0, upcomingEvents.length - upcomingTripCount);
  const eventsHeaderSubtitle = (() => {
    if (totalCount === 0) return "Trips, occasions, and events — all connected to your household";
    if (upcomingEvents.length === 0) {
      return pastEvents.length > 0
        ? `No upcoming plans · ${pastEvents.length} past saved`
        : "No upcoming plans yet";
    }
    const parts = [`${upcomingEvents.length} upcoming`];
    if (upcomingTripCount > 0) parts.push(`${upcomingTripCount} trip${upcomingTripCount !== 1 ? "s" : ""}`);
    if (upcomingOccasionCount > 0) parts.push(`${upcomingOccasionCount} occasion${upcomingOccasionCount !== 1 ? "s" : ""}`);
    if (pastEvents.length > 0) parts.push(`${pastEvents.length} past saved`);
    return parts.join(" · ");
  })();
  const nextEvent = upcomingEvents[0] || null;
  const nextEventIsTrip = (nextEvent?.occasion_type || nextEvent?.event_type) === "trip";
  const nextEventPacking = nextEvent?.trip_plan_summary || {};
  const nextEventPlan = nextEvent?.event_plan_summary || {};
  const nextPrepTaskCount = nextEventPlan.task_count || 0;
  const nextPrepDoneCount = Math.max(0, nextPrepTaskCount - (nextEventPlan.open_task_count || 0));
  const progressLabel = nextEventIsTrip
    ? (nextEventPacking.total_items ? `${nextEventPacking.packed_count}/${nextEventPacking.total_items} packed` : null)
    : (nextPrepTaskCount > 0 ? `${nextPrepDoneCount} prep done` : null);
  const nextTripPantryUseUpCount = (() => {
    if (!nextEventIsTrip || !nextEvent || nextEvent._daysUntil == null || nextEvent._daysUntil < 0 || nextEvent._daysUntil > 14) return 0;
    const departure = parseDateOnly(nextEvent.event_date || nextEvent.occasion_date || nextEvent.next_occurrence);
    if (!departure) return 0;
    return pantryItems.filter(item => {
      const expiry = parseDateOnly(item.expiry_date);
      if (!expiry) return false;
      const diffDays = Math.abs(Math.round((expiry - departure) / 86400000));
      return diffDays <= 3;
    }).length;
  })();
  const tasksDue = upcomingEvents.reduce((sum, event) => sum + (event.event_plan_summary?.open_task_count || 0), 0);
  const rsvpsPending = upcomingEvents.reduce((sum, event) => sum + (event.rsvp_maybe || 0), 0);
  const expandPastByDefault = upcomingEvents.length === 0;
  const showPastSections = expandPastByDefault || showPastEvents;

  const openNextUp = (event, tab = "") => {
    if (!event) return;
    if (event._type === "group") {
      const baseUrl = (event.event_type === "trip") ? buildGroupTripUrl(event) : buildGroupEventPlanUrl(event);
      navigate(tab ? `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}tab=${encodeURIComponent(tab)}` : baseUrl);
      return;
    }
    const isTrip = (event.occasion_type || event.event_type) === "trip";
    navigate(isTrip ? buildPersonalTripUrl(event, tab) : buildPersonalEventPlanUrl(event, tab));
  };

  const showLoading = useMinLoad(loading);
  const showDeleteNotice = useMinLoad(Boolean(deleteNoticeMessage), 700);
  const confirmDeleteEvent = confirmDeleteId
    ? personalEvents.find(event => event.id === confirmDeleteId)
    : null;

  useEffect(() => {
    if (deletingEventId) {
      setDeleteNoticeMessage("Removing event...");
    }
  }, [deletingEventId]);

  if (showLoading) return (
    <div className="loading-spinner-center">
      <span className="spinner" />
      Loading events…
    </div>
  );

  return (
    <div>
      <ModuleHeader
        title="Events & Trips"
        subtitle={eventsHeaderSubtitle}
        image={IMG_EVENTS}
      >
        {totalCount > 0 ? (
          <>
            {upcomingEvents.length > 0 ? (
              <>
                <div className="hm-module-stat">{upcomingTripCount}<span>trips</span></div>
                <div className="hm-module-stat">{upcomingOccasionCount}<span>occasions</span></div>
                {tasksDue > 0 ? (
                  <div className="hm-module-stat hm-module-stat--alert">{tasksDue}<span>due</span></div>
                ) : null}
              </>
            ) : (
              <div className="hm-module-stat">{pastEvents.length}<span>past saved</span></div>
            )}
          </>
        ) : null}
      </ModuleHeader>
      <div className="events-page-body">
        <EventsHeroStats
          upcomingCount={upcomingEvents.length}
          nextEvent={nextEvent}
          progressLabel={progressLabel}
          tasksDue={tasksDue}
          rsvpsPending={rsvpsPending}
        />
        <NextUpCard event={nextEvent} onOpen={openNextUp} tripPantryUseUpCount={nextTripPantryUseUpCount} />
        <div className="event-add-toggle-row" style={{ display:"flex", justifyContent:"flex-end", padding:"16px 0 4px" }}>
          <button className="hm-btn hm-btn--primary" onClick={() => {
            if (showForm) { setShowForm(false); setEditingEvent(null); }
            else { setEditingEvent(null); setShowForm(true); }
          }}>
            {showForm ? "Cancel" : "+ Add event"}
          </button>
        </div>

        {error && (
          <div className="notice-banner is-error mb-16">
            <p>{error}</p>
          </div>
        )}

        {showForm && (
          <FormPresentation
            title={editingEvent ? "Edit event" : "Add event"}
            onClose={() => { setShowForm(false); setEditingEvent(null); }}
          >
            {people.length === 0 ? (
              <div style={{textAlign:"center",padding:"24px 0"}}>
                <p className="text-muted mb-16">Add at least one person to your people list first.</p>
                <button className="btn btn-primary" onClick={() => navigate("/app/people")}>Go to My people</button>
              </div>
            ) : (
              <EventForm people={people} groups={groups} currentUser={user}
                initial={editingEvent ? parsePersonalEventInitial(editingEvent) : null}
                onSave={handleSave} onCancel={() => { setShowForm(false); setEditingEvent(null); }} saving={saving} />
            )}
          </FormPresentation>
        )}

        {totalCount === 0 && !showForm ? (
          <div className="card empty-state-panel">
            <div className="empty-state-icon">✈</div>
            <h3 className="empty-state-title">No events yet</h3>
            <p className="text-muted mb-16">Add a trip or event to get started.</p>
            <button className="btn btn-primary btn-lg" onClick={() => setShowForm(true)}>Add your first event</button>
          </div>
        ) : (
          <>
            {upcomingSections.length > 0 && (
              <div className="timeline-section" style={{ marginBottom: 36 }}>
                <h3 className="subsection-title timeline-heading">Upcoming</h3>
                {upcomingSections.map(section => (
                  <div key={section.key} className="timeline-group" style={{ marginBottom: 32 }}>
                    <h4 className="subsection-title timeline-subheading">{section.title}</h4>
                    <div className="page-list-stack">
                      {section.items.map(e => e._type === "group"
                        ? <GroupEventCard key={`g-${e.id}`} event={e} isFocused={focusedCardKey === `group-${e.id}`} onNavigate={navigate} onOpenRegistry={openEventRegistry} />
                        : <EventCard key={`p-${e.id}`} event={e} isFocused={focusedCardKey === `personal-${e.id}`} canShareFromHere={canShareFromHere} canGroupFromHere={canGroupFromHere} onShareCopy={openShare} onPlan={handlePlan} onInvite={handleInvite} onEdit={(event) => {
                          setEditingEvent(event);
                          setShowForm(true);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }} onDelete={setConfirmDeleteId} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {pastSections.length > 0 && (
              <div className="timeline-section is-past" style={{ marginBottom: 12 }}>
                <div className="events-past-toggle-row">
                  <h3 className="subsection-title timeline-heading">Past</h3>
                  {!expandPastByDefault ? (
                    <button type="button" className="events-past-toggle" onClick={() => setShowPastEvents(value => !value)}>
                      {showPastEvents ? "Hide past" : `Show ${pastEvents.length} past`}
                    </button>
                  ) : null}
                </div>
                {showPastSections && pastSections.map(section => (
                  <div key={section.key} className="timeline-group" style={{ marginBottom: 32 }}>
                    <h4 className="subsection-title timeline-subheading">{section.title.replace("Earlier ", "Past ")}</h4>
                    <div className="page-list-stack">
                      {section.items.map(e => e._type === "group"
                        ? <GroupEventCard key={`g-past-${e.id}`} event={e} isPast isFocused={focusedCardKey === `group-${e.id}`} onNavigate={navigate} onOpenRegistry={openEventRegistry} />
                        : <EventCard key={`p-past-${e.id}`} event={e} isPast isFocused={focusedCardKey === `personal-${e.id}`} canShareFromHere={canShareFromHere} canGroupFromHere={canGroupFromHere} onShareCopy={openShare} onPlan={handlePlan} onInvite={handleInvite} onEdit={(event) => {
                          setEditingEvent(event);
                          setShowForm(true);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }} onDelete={setConfirmDeleteId} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {confirmDeleteId && (
        <DangerConfirmModal
          tag="⚠ Remove event"
          title={`Remove ${confirmDeleteEvent ? getEventDisplayTitle(confirmDeleteEvent) : "event"}?`}
          message="This event will no longer appear for planning, reminders, or RSVPs."
          confirmLabel="Remove event"
          onCancel={() => setConfirmDeleteId(null)}
          onConfirm={async () => {
            const id = confirmDeleteId;
            setConfirmDeleteId(null);
            await handleDelete(id);
          }}
          loading={deletingEventId === confirmDeleteId}
        />
      )}

      {inviteEvent && (
        <ConfirmModal
          className="event-rsvp-modal"
          title={inviteEvent.share_slug ? "RSVP responses" : "Invite guests"}
          message={
            <>
              <p className="text-muted mb-16">
                Guests respond from the invite link. Responses stay here for planning.
              </p>
              {inviteEvent.share_slug && (
                <>
                  <div className="event-rsvp-tabs">
                    {rsvpTabs.map(tab => (
                      <button
                        key={tab.key}
                        type="button"
                        className={`event-rsvp-tab${rsvpFilter === tab.key ? " is-active" : ""}`}
                        onClick={() => setRsvpFilter(tab.key)}
                      >
                        <span>{tab.label}</span>
                        <strong>{tab.count}</strong>
                      </button>
                    ))}
                  </div>
                  <div className="event-rsvp-list">
                    {rsvpLoading ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--ink-muted)", fontSize: 14 }}>
                        <span className="spinner" /> Loading responses...
                      </div>
                    ) : filteredRsvps.length > 0 ? (
                      <div className="event-rsvp-rows">
                        {filteredRsvps.map(rsvp => (
                          <div key={rsvp.id} className="event-rsvp-row">
                            <div className="event-rsvp-main">
                              <strong>{rsvp.name}</strong>
                              <span>
                                {[rsvp.guest_count > 1 ? `${rsvp.guest_count} guests` : "1 guest", rsvp.dietary_notes].filter(Boolean).join(" · ")}
                              </span>
                            </div>
                            <span className={`event-rsvp-status is-${rsvp.status}`}>
                              {rsvp.status === "in" ? "Coming" : rsvp.status === "maybe" ? "Maybe" : "Can't make it"}
                            </span>
                            {rsvp.note ? <em>{rsvp.note}</em> : null}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-muted">
                        {rsvpFilter === "all" ? "No RSVP responses yet." : "No responses with this status yet."}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          }
          confirmLabel="Copy link"
          secondaryAction={{
            label: "Preview guest page",
            onClick: () => navigate(`/event/${inviteEvent.share_slug}`),
          }}
          onClose={() => {
            setInviteEvent(null);
            setInviteUrl("");
            setRsvpDetails(null);
          }}
          onConfirm={copyInviteLink}
        />
      )}

      {shareEvent && (
        <ConfirmModal
          title="Share with Family"
          message={
            <div className="event-family-share-panel">
              <div className="event-family-share-summary">
                <span>{(shareEvent.event_type || shareEvent.occasion_type) === "trip" ? "✈" : "📅"}</span>
                <div>
                  <strong>{shareEvent.title || shareEvent.destination || "Event"}</strong>
                  <small>Only this event will be shared.</small>
                </div>
              </div>
              <div className="form-group event-family-share-field">
                <label className="form-label">Family member</label>
                <select className="form-select" value={shareTargetId} onChange={e => setShareTargetId(e.target.value)}>
                  {shareMembers.map(member => (
                    <option key={member.user_id} value={member.user_id}>{member.full_name} - {member.email}</option>
                  ))}
                </select>
              </div>
              {shareError && (
                <div className="notice-banner is-error mt-16">
                  <p>{shareError}</p>
                </div>
              )}
            </div>
          }
          confirmLabel={shareSaving ? "Sharing..." : "Share with Family"}
          className="event-share-modal"
          onClose={() => { setShareEvent(null); setShareError(null); }}
          onConfirm={handleShare}
        />
      )}

      {shareSuccess && (
        <ConfirmModal
          title={shareSuccess.title}
          message={shareSuccess.message}
          confirmLabel="OK"
          onClose={() => setShareSuccess(null)}
          onConfirm={() => setShareSuccess(null)}
        />
      )}

      {showDeleteNotice && <SavingNotice title="Removing" message={deleteNoticeMessage || "Removing event..."} />}
      {showEventActionWorking && (
        <SavingNotice
          title={copyingInvite ? "Copying link" : shareSaving ? "Sharing" : deletingEventId ? "Removing" : "Working"}
          message={copyingInvite ? "Copying RSVP link..." : shareSaving ? "Sharing with Family..." : deletingEventId ? "Removing event..." : "Saving event..."}
        />
      )}
      {!showForm && !confirmDeleteId && !inviteEvent && !shareEvent && !shareSuccess && !showEventActionWorking ? (
        <EventsAskBar nextEvent={nextEvent} upcomingCount={upcomingEvents.length} pastCount={pastEvents.length} />
      ) : null}
    </div>
  );
}
